/**
 * Layout Service for Poker Reborn
 *
 * Handles responsive layout and screen adaptation.
 */

import { EventEmitter } from './EventEmitter';
import {
  DESIGN_RESOLUTION,
  ASPECT_RATIO,
  toPixels,
  getSeatPixelPosition,
} from '@/config/layout';
import type {
  Point,
  Size,
  Rect,
  Orientation,
  LayoutMode,
} from '@/types/ui';
import type { TableSize } from '@/types/game';

// =============================================================================
// TYPES
// =============================================================================

/** Layout service events */
export interface LayoutEvents {
  /** Screen size changed */
  resize: LayoutMode;

  /** Orientation changed */
  orientationChange: Orientation;
}

// =============================================================================
// LAYOUT SERVICE
// =============================================================================

export class LayoutService extends EventEmitter<LayoutEvents> {
  private static instance: LayoutService | null = null;

  private containerSize: Size = { width: 0, height: 0 };
  private scale = 1;
  private offset: Point = { x: 0, y: 0 };
  private safeArea: Rect = { x: 0, y: 0, width: 0, height: 0 };
  private orientation: Orientation = 'landscape';

  private resizeObserver: ResizeObserver | null = null;
  private container: HTMLElement | null = null;

  private constructor() {
    super();
  }

  /** Get singleton instance */
  static getInstance(): LayoutService {
    if (!LayoutService.instance) {
      LayoutService.instance = new LayoutService();
    }
    return LayoutService.instance;
  }

  /**
   * Initialize layout service with container element
   */
  init(container: HTMLElement): void {
    this.container = container;

    // Initial calculation
    this.updateLayout();

    // Listen for resize
    this.resizeObserver = new ResizeObserver(() => {
      this.updateLayout();
    });
    this.resizeObserver.observe(container);

    // Listen for orientation change
    window.addEventListener('orientationchange', () => {
      // Delay to allow for layout to settle
      setTimeout(() => { this.updateLayout(); }, 100);
    });
  }

  /**
   * Destroy layout service
   */
  destroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.container = null;
  }

  /**
   * Get current layout mode
   */
  getLayoutMode(): LayoutMode {
    return {
      screenWidth: this.containerSize.width,
      screenHeight: this.containerSize.height,
      orientation: this.orientation,
      scale: this.scale,
      safeArea: this.safeArea,
    };
  }

  /**
   * Get container size
   */
  getContainerSize(): Size {
    return { ...this.containerSize };
  }

  /**
   * Set screen size manually (for cases without container element)
   */
  setScreenSize(width: number, height: number): void {
    this.containerSize = { width, height };

    // Determine orientation
    this.orientation = width > height ? 'landscape' : 'portrait';

    // Calculate scale and safe area
    this.calculateScale();
    this.calculateSafeArea();

    this.emit('resize', this.getLayoutMode());
  }

  /**
   * Get current scale
   */
  getScale(): number {
    return this.scale;
  }

  /**
   * Get offset for centering
   */
  getOffset(): Point {
    return { ...this.offset };
  }

  /**
   * Get safe area (accounting for notches, etc.)
   */
  getSafeArea(): Rect {
    return { ...this.safeArea };
  }

  /**
   * Get screen width (use for BBS calculations)
   */
  getScreenWidth(): number {
    return this.containerSize.width;
  }

  /**
   * Get screen height
   */
  getScreenHeight(): number {
    return this.containerSize.height;
  }

  /**
   * Get orientation
   */
  getOrientation(): Orientation {
    return this.orientation;
  }

  /**
   * Convert design coordinates to screen coordinates
   */
  toScreen(point: Point): Point {
    return {
      x: point.x * this.scale + this.offset.x,
      y: point.y * this.scale + this.offset.y,
    };
  }

  /**
   * Convert screen coordinates to design coordinates
   */
  toDesign(point: Point): Point {
    return {
      x: (point.x - this.offset.x) / this.scale,
      y: (point.y - this.offset.y) / this.scale,
    };
  }

  /**
   * Scale a size value from design to screen
   */
  scaleSize(size: number): number {
    return size * this.scale;
  }

  /**
   * Get seat position in screen coordinates
   */
  getSeatPosition(tableSize: TableSize, seat: number): Point {
    const designPos = getSeatPixelPosition(tableSize, seat, DESIGN_RESOLUTION);
    return this.toScreen(designPos);
  }

  /**
   * Convert normalized position to screen position
   */
  normalizedToScreen(normalized: Point): Point {
    const designPos = toPixels(normalized, DESIGN_RESOLUTION);
    return this.toScreen(designPos);
  }

  /**
   * Check if point is within safe area
   */
  isInSafeArea(point: Point): boolean {
    return (
      point.x >= this.safeArea.x &&
      point.x <= this.safeArea.x + this.safeArea.width &&
      point.y >= this.safeArea.y &&
      point.y <= this.safeArea.y + this.safeArea.height
    );
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private updateLayout(): void {
    if (!this.container) return;

    const rect = this.container.getBoundingClientRect();
    this.containerSize = {
      width: rect.width,
      height: rect.height,
    };

    // Determine orientation
    const newOrientation: Orientation =
      this.containerSize.width > this.containerSize.height ? 'landscape' : 'portrait';

    const orientationChanged = newOrientation !== this.orientation;
    this.orientation = newOrientation;

    // Calculate scale (letterbox fit)
    this.calculateScale();

    // Calculate safe area
    this.calculateSafeArea();

    // Emit events
    this.emit('resize', this.getLayoutMode());

    if (orientationChanged) {
      this.emit('orientationChange', this.orientation);
    }
  }

  private calculateScale(): void {
    const containerAspect = this.containerSize.width / this.containerSize.height;

    if (containerAspect > ASPECT_RATIO) {
      // Container is wider - fit to height
      this.scale = this.containerSize.height / DESIGN_RESOLUTION.height;
      this.offset = {
        x: (this.containerSize.width - DESIGN_RESOLUTION.width * this.scale) / 2,
        y: 0,
      };
    } else {
      // Container is taller - fit to width
      this.scale = this.containerSize.width / DESIGN_RESOLUTION.width;
      this.offset = {
        x: 0,
        y: (this.containerSize.height - DESIGN_RESOLUTION.height * this.scale) / 2,
      };
    }
  }

  private calculateSafeArea(): void {
    // Start with full container
    let safeArea: Rect = {
      x: 0,
      y: 0,
      width: this.containerSize.width,
      height: this.containerSize.height,
    };

    // Check for CSS env() safe area insets (for notched devices)
    if (typeof CSS !== 'undefined' && CSS.supports && CSS.supports('padding-top: env(safe-area-inset-top)')) {
      const computedStyle = getComputedStyle(document.documentElement);

      const safeAreaTop = parseInt(computedStyle.getPropertyValue('--sat') || '0', 10);
      const safeAreaRight = parseInt(computedStyle.getPropertyValue('--sar') || '0', 10);
      const safeAreaBottom = parseInt(computedStyle.getPropertyValue('--sab') || '0', 10);
      const safeAreaLeft = parseInt(computedStyle.getPropertyValue('--sal') || '0', 10);

      safeArea = {
        x: safeAreaLeft,
        y: safeAreaTop,
        width: this.containerSize.width - safeAreaLeft - safeAreaRight,
        height: this.containerSize.height - safeAreaTop - safeAreaBottom,
      };
    }

    this.safeArea = safeArea;
  }
}

/** Singleton layout service instance */
export const layoutService = LayoutService.getInstance();
